// src/pages/SuscripcionesAdmin.js
import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

// Función para obtener el badge según el estado
const getEstadoBadge = (estado) => {
  const estados = {
    activo: { class: 'badge-success', text: '✓ Activo' },
    suspendido: { class: 'badge-warning', text: '⏸ Suspendido' },
    vencido: { class: 'badge-warning', text: '⚠️ Vencido' },
    cancelado: { class: 'badge-danger', text: '❌ Cancelado' },
    trial: { class: 'badge-info', text: '🆓 Trial' }
  };
  return estados[estado] || { class: 'badge-secondary', text: estado };
};

// Función para calcular días restantes
const getDiasRestantes = (fechaFin) => {
  if (!fechaFin) return 'Sin fecha';
  const hoy = new Date();
  const vencimiento = new Date(fechaFin);
  const diff = Math.ceil((vencimiento - hoy) / (1000 * 60 * 60 * 24));
  if (diff < 0) return 'Vencido';
  if (diff <= 3) return `${diff} días (⚠️ pronto)`;
  return `${diff} días`;
};

const SuscripcionesAdmin = () => {
  const [empresas, setEmpresas] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);
  const [pagoData, setPagoData] = useState({ dias: 30, plan_id: '' });

  const [showNuevaSuscModal, setShowNuevaSuscModal] = useState(false);
  const [nuevaEmpresa, setNuevaEmpresa] = useState({
    nombre_negocio: '',
    tipo_negocio: '',
    email: '',
    telefono: '',
    plan_id: '',
  });
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    fetchEmpresas();
    fetchPlanes();
  }, []);

  const fetchEmpresas = async () => {
    try {
      const { data } = await api.get('/admin/tenants');
      setEmpresas(data);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanes = async () => {
    try {
      const { data } = await api.get('/planes');
      setPlanes(data);
    } catch (error) {
      console.error('Error al cargar planes:', error);
    }
  };

  const registrarPago = async () => {
    try {
      await api.post(`/admin/tenants/${empresaSeleccionada.id}/pago`, {
        dias: pagoData.dias,
        plan_id: pagoData.plan_id
      });
      toast.success(`✅ Pago registrado para ${empresaSeleccionada.nombre_negocio}`);
      setShowPagoModal(false);
      fetchEmpresas();
    } catch (error) {
      console.error('Error al registrar pago:', error);
      toast.error('Error al registrar pago');
    }
  };

  const crearNuevaEmpresa = async () => {
    if (!nuevaEmpresa.nombre_negocio || !nuevaEmpresa.email || !nuevaEmpresa.plan_id) {
      toast.warning('Nombre, email y plan son requeridos');
      return;
    }
    try {
      await api.post('/admin/tenants/crear', {
        ...nuevaEmpresa,
        dias: 7,
      });
      toast.success(`✅ Empresa "${nuevaEmpresa.nombre_negocio}" creada — 7 días activos`);
      setShowNuevaSuscModal(false);
      setNuevaEmpresa({ nombre_negocio: '', tipo_negocio: '', email: '', telefono: '', plan_id: '' });
      fetchEmpresas();
    } catch (error) {
      console.error('Error al crear empresa:', error);
      toast.error(error.response?.data?.error || 'Error al crear la empresa');
    }
  };

  const cancelarSuscripcion = (id, nombreEmpresa) => {
    setConfirmAction({ onConfirm: async () => {
      try {
        await api.post(`/admin/tenants/${id}/cancelar`);
        toast.success(`❌ Suscripción cancelada para ${nombreEmpresa}`);
        fetchEmpresas();
      } catch (error) {
        console.error('Error al cancelar suscripción:', error);
        toast.error('Error al cancelar suscripción');
      }
    }});
  };

  const suspenderEmpresa = (id, nombreEmpresa) => {
    setConfirmAction({ onConfirm: async () => {
      try {
        await api.post(`/admin/tenants/${id}/suspender`);
        toast.success(`⏸ Empresa suspendida: ${nombreEmpresa}`);
        fetchEmpresas();
      } catch (error) {
        console.error('Error al suspender empresa:', error);
        toast.error('Error al suspender empresa');
      }
    }});
  };

  const activarEmpresa = async (id, nombreEmpresa) => {
    try {
      await api.post(`/admin/tenants/${id}/activar`);
      toast.success(`✓ Empresa activada: ${nombreEmpresa}`);
      fetchEmpresas();
    } catch (error) {
      console.error('Error al activar empresa:', error);
      toast.error('Error al activar empresa');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading-screen">
          <div className="loading"></div>
          <p>Cargando suscripciones...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="admin-dashboard">
        <div className="page-header">
          <div>
            <h2>💰 Suscripciones de Empresas</h2>
            <p className="page-subtitle">Gestiona los pagos y estados de suscripción</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowNuevaSuscModal(true)}>
            🏢 Nueva Empresa
          </button>
        </div>

        <div className="card">
          <div className="card-title">📋 Empresas Registradas</div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Empresa</th>
                  <th>Código</th>
                  <th>Plan</th>
                  <th>Estado</th>
                  <th>Vencimiento</th>
                  <th>Clientes</th>
                  <th>Citas</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {empresas.map(empresa => {
                  const estadoBadge = getEstadoBadge(empresa.estado_suscripcion);
                  const diasRestantes = getDiasRestantes(empresa.fecha_fin_suscripcion);
                  
                  return (
                    <tr key={empresa.id} className={empresa.estado_suscripcion === 'cancelado' ? 'cancelled-row' : ''}>
                      <td>{empresa.id}</td>
                      <td>
                        <strong>{empresa.nombre_negocio}</strong><br />
                        <small>{empresa.email}</small>
                      </td>
                      <td><code>{empresa.codigo_tenant}</code></td>
                      <td>
                        <span className="plan-badge">{empresa.plan_nombre || 'Sin plan'}</span>
                      </td>
                      <td>
                        <span className={`badge ${estadoBadge.class}`}>
                          {estadoBadge.text}
                        </span>
                      </td>
                      <td className={diasRestantes === 'Vencido' ? 'danger-text' : diasRestantes.includes('pronto') ? 'warning-text' : ''}>
                        {diasRestantes}
                      </td>
                      <td>{empresa.total_clientes || 0}</td>
                      <td>{empresa.total_citas || 0}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          {empresa.estado_suscripcion !== 'cancelado' && (
                            <>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => {
                                  setEmpresaSeleccionada(empresa);
                                  setPagoData({ dias: 30, plan_id: empresa.plan_id || 1 });
                                  setShowPagoModal(true);
                                }}
                                title="Registrar pago"
                                style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                              >
                                💰
                              </button>
                              {empresa.estado_suscripcion !== 'suspendido' ? (
                                <button
                                  className="btn btn-sm btn-warning"
                                  onClick={() => suspenderEmpresa(empresa.id, empresa.nombre_negocio)}
                                  title="Suspender empresa"
                                  style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                                >
                                  ⏸
                                </button>
                              ) : (
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => activarEmpresa(empresa.id, empresa.nombre_negocio)}
                                  title="Activar empresa"
                                  style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                                >
                                  ✓
                                </button>
                              )}
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => cancelarSuscripcion(empresa.id, empresa.nombre_negocio)}
                                title="Cancelar suscripción"
                                style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                              >
                                ✕
                              </button>
                            </>
                          )}
                          {empresa.estado_suscripcion === 'cancelado' && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => {
                                setEmpresaSeleccionada(empresa);
                                setPagoData({ dias: 30, plan_id: empresa.plan_id || 1 });
                                setShowPagoModal(true);
                              }}
                              title="Reactivar con nuevo pago"
                              style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                            >
                              🔄
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal para registrar pago */}
        {showPagoModal && empresaSeleccionada && (
          <div className="modal-overlay">
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                padding: '1.5rem 1.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    width: '42px', height: '42px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.3rem',
                  }}>
                    💰
                  </div>
                  <div>
                    <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                      Registrar Pago - {empresaSeleccionada.nombre_negocio}
                    </h3>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                      Registra el pago de suscripción
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPagoModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    width: '32px', height: '32px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >✕</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Plan a asignar</label>
                  <select
                    className="form-select"
                    value={pagoData.plan_id}
                    onChange={(e) => setPagoData({...pagoData, plan_id: e.target.value})}
                  >
                    {planes.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {plan.nombre} - ${plan.precio_mensual}/mes
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Período de suscripción</label>
                  <select
                    className="form-select"
                    value={pagoData.dias}
                    onChange={(e) => setPagoData({...pagoData, dias: parseInt(e.target.value)})}
                  >
                    <option value={7}>7 días</option>
                    <option value={15}>15 días</option>
                    <option value={30}>30 días (1 mes)</option>
                    <option value={60}>60 días (2 meses)</option>
                    <option value={90}>90 días (3 meses)</option>
                    <option value={180}>180 días (6 meses)</option>
                    <option value={365}>365 días (1 año)</option>
                  </select>
                </div>
                <div className="info-box">
                  <p>📌 Al registrar el pago:</p>
                  <ul>
                    <li>La empresa quedará ACTIVA automáticamente</li>
                    <li>La suscripción se extenderá por el período seleccionado</li>
                    <li>Se asignará el plan elegido</li>
                    <li>El cliente podrá acceder al sistema normalmente</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowPagoModal(false)}>
                  Cancelar
                </button>
                <button className="btn btn-primary" onClick={registrarPago}>
                  Registrar Pago
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Modal nueva empresa */}
        {showNuevaSuscModal && (
          <div className="modal-overlay">
            <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                padding: '1.5rem 1.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    width: '42px', height: '42px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.3rem',
                  }}>
                    💰
                  </div>
                  <div>
                    <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                      Nueva Empresa
                    </h3>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                      Completa los datos para agregar una nueva empresa
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowNuevaSuscModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    width: '32px', height: '32px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >✕</button>
              </div>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Nombre del negocio *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Ej: Salón de Belleza Ana"
                      value={nuevaEmpresa.nombre_negocio}
                      onChange={e => setNuevaEmpresa({ ...nuevaEmpresa, nombre_negocio: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Tipo de negocio</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Ej: Peluquería, Clínica, Spa"
                      value={nuevaEmpresa.tipo_negocio}
                      onChange={e => setNuevaEmpresa({ ...nuevaEmpresa, tipo_negocio: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="correo@empresa.com"
                      value={nuevaEmpresa.email}
                      onChange={e => setNuevaEmpresa({ ...nuevaEmpresa, email: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Teléfono</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="+593 99 999 9999"
                      value={nuevaEmpresa.telefono}
                      onChange={e => setNuevaEmpresa({ ...nuevaEmpresa, telefono: e.target.value })}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Plan a asignar *</label>
                    <select
                      className="form-select"
                      value={nuevaEmpresa.plan_id}
                      onChange={e => setNuevaEmpresa({ ...nuevaEmpresa, plan_id: e.target.value })}
                    >
                      <option value="">— Selecciona un plan —</option>
                      {planes.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.nombre} — ${p.precio_mensual}/mes
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="info-box" style={{ marginTop: '1rem' }}>
                  <p>⏱️ La empresa quedará <strong>Activa por 7 días</strong> hasta que el cliente realice su primer pago. Se generará automáticamente un código de acceso.</p>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowNuevaSuscModal(false)}>
                  Cancelar
                </button>
                <button className="btn btn-primary" onClick={crearNuevaEmpresa}>
                  🏢 Crear empresa y activar 7 días
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '700' }}>
                  Confirmar acción
                </h3>
              </div>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '28px', height: '28px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#374151' }}>¿Confirmas esta acción?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default SuscripcionesAdmin;
